import { motion } from 'motion/react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { Award, Shield, Zap, Users, Target, CheckCircle2, Scale, ShieldCheck, LucideProps, Globe, Flag, MapPin } from 'lucide-react';
import { ForwardRefExoticComponent, RefAttributes } from 'react';

export const WhyClientsChooseUsSection = () => {
  const { ref, isVisible } = useScrollAnimation(0.1);

  const reasons = [
    {
      icon: Shield,
      title: 'Local Market Mastery',
      description:
        'In-depth knowledge of Saudi regulations, regulatory authorities, and legal procedures ensuring compliant and reliable outcomes.',
      stat: '15+',
      statLabel: 'Years in KSA',
    },
    {
      icon: Users,
      title: 'Dedicated Client Partners',
      description:
        'Direct access to senior lawyers with consistent engagement and tailored legal guidance for every client.',
      stat: '98%',
      statLabel: 'Client Retention',
    },
    {
      icon: Target,
      title: 'Results-Oriented Legal Advice',
      description:
        'Practical legal solutions aligned with commercial realities, designed to protect interests and manage risk effectively.',
      stat: '1,200+',
      statLabel: 'Matters Handled',
    },
    {
      icon: Award,
      title: 'Industry Specialists',
      description:
        'Sector-specific legal expertise across energy, technology, manufacturing, and infrastructure with proven experience.',
      stat: '10+',
      statLabel: 'Industries',
    },
    {
      icon: Zap,
      title: 'Responsive Legal Support',
      description:
        'Timely, well-considered legal advice with clear communication, ensuring clients are supported at every stage.',
      stat: '24–48 hr',
      statLabel: 'Response Time',
    },
  ];


    const whyLocalExpertise: {
    title: 'Bilingual Excellence' | 'Cultural Intelligence' | 'Local Presence'; // REMOVED Government Relations
    description: string;
  }[] = [
    {
      title: 'Bilingual Excellence',
      description: 'Fluent representation in both Arabic and English for seamless communication with local authorities',
    },
    {
      title: 'Cultural Intelligence',
      description: 'Deep understanding of business customs, negotiation practices, and decision-making protocols',
    },
    {
      title: 'Local Presence',
      description: 'Based in Riyadh with nationwide reach across all major Saudi commercial centers',
    },
  ];

  // Type-safe icon map - UPDATED to remove Government Relations
  const iconMap: Record<
    typeof whyLocalExpertise[number]['title'],
    ForwardRefExoticComponent<LucideProps & RefAttributes<SVGSVGElement>>
  > = {
    'Bilingual Excellence': Globe,
    'Cultural Intelligence': Flag,
    'Local Presence': MapPin,
  };
  
  return (
    <section className="relative bg-[#0a0a0a] py-12 sm:py-14 lg:py-12 overflow-hidden w-full min-h-screen flex items-center">
      {/* Premium Background Pattern */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, rgba(195, 165, 101, 1) 1px, transparent 0)`,
          backgroundSize: '48px 48px',
        }}></div>
      </div>

      {/* Accent Gradients */}
      <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-[#c3a565]/5 blur-[120px] rounded-full"></div>
      <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-[#c3a565]/5 blur-[120px] rounded-full"></div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" ref={ref}>
        {/* Section Header */}
        <div className="mb-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="flex items-center justify-center gap-4 mb-3"
          >
            <div className="h-[1px] w-12 sm:w-16 bg-[#c3a565]"></div>
            <div className="flex items-center gap-2">
              <Award className="w-4 h-4 text-[#c3a565]" strokeWidth={2} />
              <span className="text-xs sm:text-sm tracking-[0.25em] sm:tracking-[0.3em] uppercase text-[#c3a565]" style={{ fontWeight: 700 }}>
                Our Advantage
              </span>
            </div>
            <div className="h-[1px] w-12 sm:w-16 bg-[#c3a565]"></div>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-[#faf9f7] text-2xl sm:text-3xl lg:text-4xl mb-2"
            style={{ fontWeight: 600 }}
          >
            Why Clients Choose Us
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-[#faf9f7]/60 max-w-3xl mx-auto text-sm sm:text-base"
            style={{ fontWeight: 300 }}
          >
            Trusted by leading corporates and investors for our unique combination of international standards and local expertise
          </motion.p>
        </div>

        {/* Reasons Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 mb-6 sm:mb-8">
          {reasons.map((reason, index) => {
            const Icon = reason.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={isVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.1 * index }}
                className="group"
              >
                <div className="bg-[#1a1a1a] border border-[#c3a565]/10 rounded-xl p-4 sm:p-5 hover:border-[#c3a565]/40 hover:shadow-[0_8px_40px_rgba(195,165,101,0.15)] hover:scale-[1.02] hover:bg-[#1a1a1a]/90 transition-all duration-500 h-full flex flex-col">
                  {/* Icon & Stat */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="p-2.5 bg-[#c3a565]/10 rounded-lg group-hover:bg-[#c3a565]/20 group-hover:scale-110 transition-all duration-500">
                      <Icon className="w-5 h-5 text-[#c3a565] group-hover:text-[#d4b676] transition-colors duration-500" strokeWidth={1.5} />
                    </div>
                    <div className="text-right">
                      <div className="text-[#c3a565] text-lg font-semibold leading-none">
                        {reason.stat}
                      </div>
                      <div className="text-[#faf9f7]/40 text-md">
                        {reason.statLabel}
                      </div>
                    </div>
                  </div>

                  {/* Title */}
                  <h3 
                    className="text-[#faf9f7] mb-3 text-lg font-semibold group-hover:text-[#c3a565] transition-colors"
                  >
                    {reason.title}
                  </h3>

                  {/* Description */}
                  <p className="text-[#faf9f7]/60 text-md leading-relaxed">
                    {reason.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Trust Markers Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
            <div className="flex items-center gap-2">
              <Scale className="w-4 h-4 text-[#c3a565] licensed-icons" strokeWidth={3} fill="#c3a565" fillOpacity={0.2} />
              <span className="text-[#faf9f7]/80 whitespace-nowrap" style={{ fontWeight: 400, fontSize: '1rem' }}>
                Licensed by Ministry of Justice
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Award className="w-4 h-4 text-[#c3a565] licensed-icons" strokeWidth={3} fill="#c3a565" fillOpacity={0.2} />
              <span className="text-[#faf9f7]/80 whitespace-nowrap" style={{ fontWeight: 400, fontSize: '1rem' }}>
                Member of Saudi Bar Association
              </span>
            </div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-[#c3a565] licensed-icons" strokeWidth={3} fill="#c3a565" fillOpacity={0.2} />
              <span className="text-[#faf9f7]/80 whitespace-nowrap" style={{ fontWeight: 400, fontSize: '1rem' }}>
                Confidentiality Guaranteed
              </span>
            </div>
          </div>
        </motion.div>

        
        {/* Why Local Expertise Matters - UPDATED with 3 items only */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className='relative why-local-padding overflow-hidden w-full'
        >

          <div className="text-center mb-10">
            <h3 
              className="text-[#FAF9F7] mb-2 pt-5"
              style={{ 
                fontWeight: 600, fontSize: '2rem' }}
            >
              Why Local Expertise Matters
            </h3>
            <p className="text-[#faf9f7]/60" style={{ fontWeight: 300, fontSize: '0.95rem' }}>
              Navigate the Saudi legal landscape with confidence
            </p>
          </div>

          {/* UPDATED: 3 column grid instead of 4 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {whyLocalExpertise.map((item, index) => {
              const Icon = iconMap[item.title];

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-[#1a1a1a] border border-[#c3a565]/10 rounded-xl p-4 sm:p-5 hover:border-[#c3a565]/40 hover:shadow-[0_8px_40px_rgba(195,165,101,0.15)]  hover:bg-[#1a1a1a]/90 transition-all duration-500 h-full flex flex-col text-start items-start"
                >
                  {Icon && <Icon className="text-[#c3a565] w-8 h-8 mb-4" />}
                  <h4 className="text-[#faf9f7] mb-3" style={{ fontWeight: 600, fontSize: '1.2rem' }}>
                    {item.title}
                  </h4>
                  <p className="text-[#faf9f7]/60 leading-relaxed" style={{ fontWeight: 300, fontSize: '0.9rem' }}>
                    {item.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Stats Banner */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-16 sm:mt-20"
        >
          <div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-12 text-center">
              <div>
                <div className="text-[#c3a565] mb-1" style={{ 
                  fontWeight: 600, fontSize: '2.5rem', lineHeight: '1' }}>
                  15+
                </div>
                <div className="text-[#faf9f7]/60  text-sm" style={{ fontWeight: 400 }}>
                  Years in Saudi Arabia
                </div>
              </div>
              <div className="hidden sm:block w-px h-12 bg-[#c3a565]/30"></div>
              <div>
                <div className="text-[#c3a565] mb-1" style={{ 
                  fontWeight: 600, fontSize: '2.5rem', lineHeight: '1' }}>
                  500+
                </div>
                <div className="text-[#faf9f7]/60 text-sm" style={{ fontWeight: 400 }}>
                  Saudi Regulatory Approvals
                </div>
              </div>
              <div className="hidden sm:block w-px h-12 bg-[#c3a565]/30"></div>
              <div>
                <div className="text-[#c3a565] mb-1" style={{ 
                  fontWeight: 600, fontSize: '2.5rem', lineHeight: '1' }}>
                  100%
                </div>
                <div className="text-[#faf9f7]/60 text-sm" style={{ fontWeight: 400 }}>
                  Licensed & Accredited
                </div>
              </div>
            </div>
          </div>
        </motion.div>


      </div>
    </section>
  );
};